import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { fmtDateTime } from '../lib/format';
import { useAuth } from '../stores/auth.store';
import { DutyLogDetailDialog } from '../components/DutyLogDetailDialog';
import { Pagination } from '../components/Pagination';

const PAGE_SIZE = 15;

interface Unit {
  id: string;
  name: string;
}

interface Photo {
  id: string;
  phase: 'start' | 'end';
  seq: number;
  url: string;
  thumbnailUrl: string;
}

interface DutyLog {
  id: string;
  locationName: string;
  startAt: string;
  status: 'in_progress' | 'completed' | 'cancelled';
  dutyType: { name: string };
  officer?: { firstName: string; lastName: string };
  photos?: Photo[];
}

const STATUS_LABEL: Record<DutyLog['status'], string> = {
  in_progress: 'กำลังดำเนินการ',
  completed: 'เสร็จสิ้น',
  cancelled: 'ยกเลิก',
};

export function SupervisorDutyLogsPage() {
  const officer = useAuth((s) => s.officer);
  const navigate = useNavigate();
  const [items, setItems] = useState<DutyLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [unitId, setUnitId] = useState('');
  const [units, setUnits] = useState<Unit[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<Unit[]>('/app/sv/units')
      .then((r) => setUnits(r.data))
      .catch(() => undefined);
  }, []);

  async function load() {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, pageSize: PAGE_SIZE };
      if (search) params.search = search;
      if (status) params.status = status;
      if (unitId) params.unitId = unitId;
      const { data } = await api.get<{ data: DutyLog[]; total: number }>('/app/sv/duty-logs', {
        params,
      });
      setItems(data.data);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [page]);

  function applySearch() {
    setPage(1);
    load();
  }

  function changeStatus(s: string) {
    setStatus(s);
    setPage(1);
  }
  function changeUnit(u: string) {
    setUnitId(u);
    setPage(1);
  }

  if (officer && officer.role === 'officer') {
    return <Navigate to="/" replace />;
  }

  const activeFilters = [status, unitId].filter(Boolean).length;

  return (
    <div className="mx-auto min-h-screen max-w-md bg-gray-50 pb-24">
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-gray-100 bg-white px-4 py-3">
        <button onClick={() => navigate(-1)} className="text-2xl text-gray-500">
          ‹
        </button>
        <div>
          <h1 className="text-base font-semibold">บันทึกทั้งหมด</h1>
          <p className="text-xs text-gray-500">ทั้งหมด {total} รายการ</p>
        </div>
      </header>

      <main className="space-y-3 p-4">
        <div className="card space-y-3">
          <div className="flex gap-2">
            <input
              className="input flex-1"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applySearch()}
              placeholder="ค้นหาสถานที่ / ชื่อ"
            />
            <button onClick={applySearch} className="rounded-xl bg-line px-4 text-sm font-medium text-white active:bg-line-dark">
              ค้นหา
            </button>
          </div>
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="text-xs text-gray-500 underline"
          >
            ตัวกรอง {activeFilters > 0 && `(${activeFilters})`}
          </button>
          {filtersOpen && (
            <div className="grid grid-cols-2 gap-2 border-t border-gray-100 pt-3">
              <div>
                <label className="label">สถานะ</label>
                <select className="input" value={status} onChange={(e) => changeStatus(e.target.value)}>
                  <option value="">ทั้งหมด</option>
                  <option value="in_progress">กำลังดำเนินการ</option>
                  <option value="completed">เสร็จสิ้น</option>
                  <option value="cancelled">ยกเลิก</option>
                </select>
              </div>
              <div>
                <label className="label">สังกัด</label>
                <select className="input" value={unitId} onChange={(e) => changeUnit(e.target.value)}>
                  <option value="">ทุกสังกัด</option>
                  {units.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          {loading ? (
            <div className="card p-6 text-center text-sm text-gray-500">กำลังโหลด...</div>
          ) : items.length === 0 ? (
            <div className="card p-6 text-center text-sm text-gray-500">ไม่พบข้อมูล</div>
          ) : (
            items.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => setSelectedId(l.id)}
                className="card flex w-full items-start gap-3 text-left active:bg-gray-50"
              >
                {l.photos && l.photos.length > 0 ? (
                  <div className="relative shrink-0">
                    <img
                      src={l.photos[0]!.thumbnailUrl}
                      alt=""
                      className="h-16 w-16 rounded-lg object-cover ring-1 ring-gray-200"
                    />
                    {l.photos.length > 1 && (
                      <span className="absolute -bottom-1 -right-1 rounded-full bg-gray-900/80 px-1.5 py-0.5 text-[10px] font-medium text-white">
                        ×{l.photos.length}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-xs text-gray-400">
                    —
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500">{fmtDateTime(l.startAt)}</p>
                  <p className="mt-0.5 truncate text-sm font-medium">
                    {l.officer ? `${l.officer.firstName} ${l.officer.lastName}` : '-'}
                  </p>
                  <p className="mt-0.5 truncate text-sm text-line">{l.locationName}</p>
                  <div className="mt-1">
                    <span className={`badge-${l.status}`}>{STATUS_LABEL[l.status]}</span>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />
      </main>

      <DutyLogDetailDialog
        dutyLogId={selectedId}
        onClose={() => setSelectedId(null)}
        onChanged={load}
      />
    </div>
  );
}
